import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import streamLinks from "../../old-stream-links.json";
import { RootState } from "../../../../../../store/Index";
import { IStreamDetails, IStreamResponse } from "../types/memorial-types";
import { DeceasedPerson } from "../../../../../bookings/types/edit-booking";
import { useMemorialTheme } from "../hooks/useTheme";
import apiService, { BaseURL } from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";

// ─── Types ────────────────────────────────────────────────────────────────────
interface IMessage {
  id: number;
  sender_full_name: string | null;
  sender_email: string;
  content: string;
  created_at: string;
  is_approved: boolean;
  parent: number | null;
}

interface IApiMessageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IMessage[];
}

export interface ICommentType {
  id: string;
  author: string;
  date: string;
  content: string;
  is_approved?: boolean;
  replies?: ICommentType[];
  parent_message?: string;
}

interface StreamProgramResponse {
  id: number;
  file?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

type EnrichedStream = { index: number; start: Date | null; end: Date | null };
type ValidStream = { index: number; start: Date; end: Date };
const isValidStream = (s: EnrichedStream): s is ValidStream =>
  s.start instanceof Date && s.end instanceof Date;

// ─── URL → booking ID map ─────────────────────────────────────────────────────
const URL_TO_BOOKING_ID: Readonly<Record<string, string>> = Object.freeze(
  streamLinks.reduce(
    (acc, { booking_id, website_stream_link }) => {
      acc[website_stream_link] = String(booking_id);
      return acc;
    },
    {} as Record<string, string>,
  ),
);

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useConferenceStream() {
  const { id: bookingIdParam } = useParams<{ id: string }>();
  const [bookingId, setBookingId] = useState<string | undefined>(undefined);

  const auth = useSelector((state: RootState) => state.auth);
  const isAdmin =
    auth.user?.permissions.includes("chat.delete_memorialmessage") || false;

  const [streams, setStreams] = useState<IStreamDetails[]>([]);
  const [activeStreamIndex, setActiveStreamIndex] = useState(0);
  const [deceasedData, setDeceasedData] = useState<DeceasedPerson | null>(null);
  const [messages, setMessages] = useState<ICommentType[]>([]);
  const [streamProgram, setStreamProgram] = useState<string | null>(null);
  const [isLoadingStreams, setIsLoadingStreams] = useState(false);
  const [downloadingProgram, setDownloadingProgram] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const memorialTheme = useMemorialTheme(streams);
  const currentStream = streams[activeStreamIndex];

  // ── Resolve booking ID ──
  useEffect(() => {
    const mapped = URL_TO_BOOKING_ID[window.location.href];
    setBookingId(mapped ?? bookingIdParam);
  }, [bookingIdParam]);

  // ── Fetch streams ──
  const fetchStreamDetails = useCallback(async () => {
    if (!bookingId) return;
    setIsLoadingStreams(true);
    try {
      const res = await apiService.get<IStreamResponse>(
        `/api/streams/stream/?booking=${bookingId}`,
      );
      setStreams(res.data.results);
      if (res.data.results.length === 1) setActiveStreamIndex(0);
    } catch {
      showErrorToast("Failed to load stream details.", "stream-details-error");
      setStreams([]);
    } finally {
      setIsLoadingStreams(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchStreamDetails();
  }, [fetchStreamDetails]);

  // ── Auto-select stream ──
  const getServiceDate = useCallback(
    (s: IStreamDetails, field: "start" | "end") => {
      const val = s[field];
      if (!s.date || !val) return null;
      const d = new Date(`${s.date}T${val}+02:00`);
      return isNaN(d.getTime()) ? null : d;
    },
    [],
  );

  useEffect(() => {
    if (!streams.length) return;
    const now = new Date();
    const enriched: EnrichedStream[] = streams.map((s, i) => ({
      index: i,
      start: getServiceDate(s, "start"),
      end: getServiceDate(s, "end"),
    }));
    const valid = enriched.filter(isValidStream);
    if (!valid.length) return;
    const ongoing = valid.find((s) => now >= s.start && now <= s.end);
    if (ongoing) {
      setActiveStreamIndex(ongoing.index);
      return;
    }
    const upcoming = valid
      .filter((s) => s.start > now)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    if (upcoming.length) {
      setActiveStreamIndex(upcoming[0].index);
      return;
    }
    const past = valid
      .filter((s) => s.end < now)
      .sort((a, b) => b.end.getTime() - a.end.getTime());
    if (past.length) setActiveStreamIndex(past[0].index);
  }, [getServiceDate, streams]);

  // ── Fetch deceased ──
  const fetchDeceasedDetails = useCallback(async (id: number) => {
    try {
      const res = await apiService.get<DeceasedPerson>(
        `/api/individuals/deceased/${id}/retrieve-by-id/`,
        {},
        true,
      );
      setDeceasedData(res.data);
    } catch {
      showErrorToast(
        "Failed to load obituary details.",
        "obituary-details-error",
      );
    }
  }, []);

  useEffect(() => {
    if (currentStream?.booking?.deceased?.[0]?.id) {
      fetchDeceasedDetails(currentStream.booking.deceased[0].id);
    }
  }, [currentStream, fetchDeceasedDetails]);

  // ── Message helpers ──
  const buildMessageTree = useCallback(
    (msgs: ICommentType[]): ICommentType[] => {
      const map: Record<string, ICommentType> = {};
      const roots: ICommentType[] = [];
      msgs.forEach((m) => {
        map[m.id] = { ...m, replies: [] };
      });
      msgs.forEach((m) => {
        if (m.parent_message && map[m.parent_message]) {
          map[m.parent_message].replies!.push(map[m.id]);
        } else {
          roots.push(map[m.id]);
        }
      });
      const sort = (arr: ICommentType[]) => {
        arr.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
        arr.forEach((m) => {
          if (m.replies) sort(m.replies);
        });
      };
      sort(roots);
      return roots;
    },
    [],
  );

  const flattenMessages = useCallback(
    (msgs: ICommentType[]): ICommentType[] => {
      const flatten = (arr: ICommentType[]): ICommentType[] => {
        let flat: ICommentType[] = [];
        arr.forEach((m) => {
          flat.push({ ...m, replies: undefined });
          if (m.replies) flat = flat.concat(flatten(m.replies));
        });
        return flat;
      };
      return flatten(msgs);
    },
    [],
  );

  const removeById = useCallback(
    (msgs: ICommentType[], id: string): ICommentType[] =>
      msgs
        .filter((m) => m.id !== id)
        .map((m) => ({
          ...m,
          replies: m.replies ? removeById(m.replies, id) : [],
        })),
    [],
  );

  // ── Fetch messages ──
  const fetchMessages = useCallback(async () => {
    if (!currentStream?.id) return;
    try {
      let q = `stream_id=${currentStream.id}`;
      if (!isAdmin) q += "&is_approved=true";
      const res = await apiService.get<IApiMessageResponse>(
        `/api/chats/memorial-message/?${q}`,
        undefined,
        true,
      );
      const mapped = res.data.results.map((m) => ({
        id: m.id.toString(),
        author: m.sender_full_name || "Anonymous",
        date: new Date(m.created_at).toLocaleString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        content: m.content,
        is_approved: m.is_approved,
        parent_message: m.parent ? m.parent.toString() : undefined,
      }));
      setMessages(buildMessageTree(mapped));
    } catch {
      showErrorToast("Failed to load messages.", "messages-fetch-error");
    }
  }, [buildMessageTree, currentStream?.id, isAdmin]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // ── WebSocket ──
  useEffect(() => {
    if (!currentStream?.id) return;
    const wsProtocol = BaseURL.startsWith("https") ? "wss" : "ws";
    const wsHost = new URL(BaseURL).host;
    const ws = new WebSocket(
      `${wsProtocol}://${wsHost}/ws/stream-memorial/${currentStream.id}/`,
    );

    ws.onopen = () => ws.send(JSON.stringify({ type: "refetch" }));
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.error) {
          showErrorToast(data.error, "websocket-error");
          return;
        }

        if (data.type === "delete") {
          setMessages((prev) => removeById(prev, data.id));
        } else if (data.type === "all_messages") {
          const msgs = (
            data.messages as Array<{
              id: number | string;
              sender_full_name?: string | null;
              timestamp?: string;
              message?: string;
              is_approved?: boolean;
              parent_message?: number | string | null;
            }>
          ).map((m) => ({
            id: m.id.toString(),
            author: m.sender_full_name || "Anonymous",
            date: new Date(m.timestamp || Date.now()).toLocaleString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
            content: m.message || "",
            is_approved: m.is_approved,
            parent_message: m.parent_message
              ? m.parent_message.toString()
              : undefined,
          }));
          setMessages(buildMessageTree(msgs));
        } else {
          const newMsg: ICommentType = {
            id: data.id.toString(),
            author: data.sender_full_name || "Anonymous",
            date: new Date(data.timestamp || Date.now()).toLocaleString(
              "en-US",
              {
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              },
            ),
            content: data.message || "",
            is_approved: data.is_approved,
            parent_message: data.parent_message
              ? data.parent_message.toString()
              : undefined,
          };
          setMessages((prev) => {
            const flat = flattenMessages(prev);
            const idx = flat.findIndex((m) => m.id === newMsg.id);
            const updated =
              idx !== -1
                ? flat.map((m, i) => (i === idx ? newMsg : m))
                : [...flat, newMsg];
            return buildMessageTree(updated);
          });
        }
      } catch (e) {
        console.error("WS parse error", e, event.data);
      }
    };
    ws.onerror = (e) => console.error("WS error", e);
    ws.onclose = () => console.log("WS closed");
    socketRef.current = ws;
    return () => ws.close();
  }, [
    buildMessageTree,
    currentStream?.id,
    flattenMessages,
    isAdmin,
    removeById,
  ]);

  // ── Message actions ──
  const handleApprove = (id: string, approve: boolean) => {
    if (!isAdmin) {
      showErrorToast(
        "Only admins can approve messages.",
        "approve-permission-error",
      );
      return;
    }
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "approve", id, approve }));
      showSuccessToast(
        `Message ${approve ? "approved" : "disapproved"} successfully!`,
      );
    } else {
      showErrorToast("Failed to update approval.", "approve-error");
    }
  };

  const handleDelete = (id: string) => {
    if (!isAdmin) {
      showErrorToast(
        "Only admins can delete messages.",
        "delete-permission-error",
      );
      return;
    }
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "delete", id }));
      showSuccessToast("Message deleted successfully!");
    } else {
      showErrorToast("Failed to delete message.", "delete-error");
    }
  };

  // ── Program ──
  const fetchProgram = useCallback(async () => {
    if (!currentStream?.id) return;
    try {
      const res = await apiService.get<
        PaginatedResponse<StreamProgramResponse>
      >(`/api/streams/stream-program/?stream=${currentStream.id}`);
      setStreamProgram(res.data.results?.[0]?.file ?? null);
    } catch {
      /* silent */
    }
  }, [currentStream?.id]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const handleDownloadProgram = async () => {
    if (!streamProgram) {
      showErrorToast("No program available to download");
      return;
    }
    setDownloadingProgram(true);
    try {
      const response = await fetch(streamProgram, { credentials: "include" });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const cd = response.headers.get("Content-Disposition");
      const filename = cd?.includes("filename=")
        ? cd.split("filename=")[1].replace(/"/g, "")
        : decodeURIComponent(streamProgram.split("/").pop() || "program");
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showErrorToast("Failed to download program");
    } finally {
      setDownloadingProgram(false);
    }
  };

  return {
    // State
    streams,
    activeStreamIndex,
    setActiveStreamIndex,
    currentStream,
    deceasedData,
    messages,
    streamProgram,
    isLoadingStreams,
    downloadingProgram,
    isAdmin,
    memorialTheme,
    socketRef,
    // Actions
    fetchStreamDetails,
    handleApprove,
    handleDelete,
    handleDownloadProgram,
  };
}
